import React, { useMemo } from 'react';
import Tooltip from '@mui/material/Tooltip';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { CalendarIcon, LeadsIcon, PropertiesIcon, UsersIcon } from 'src/icons';
import { LEAD_INGESTION_METHOD_OPTIONS } from 'src/services/api/leads/constants';
import { leadDateFilterLabels } from 'src/pages/landlord/leads/provider';
import { useLeadsPageContext } from 'src/pages/landlord/leads/provider';
import Chip from 'src/components/chip/Chip';
import { useLeadLabels } from 'src/services/api';
import {
    getAssigneesFilterLabel,
    getIngestionSourcesFilterLabel,
    getLeadStagesFilterLabel,
    getLeadLabelsFilterLabel,
    getPropertyFilterLabel,
    getPropertyLabelsFilterLabel
} from 'src/pages/landlord/conversations/utils/filter-tabs';

export default function LeadsFilterStatus() {
    const { data: leadLabels } = useLeadLabels();

    const {
        teamMembers,
        properties,
        propertyLabels,
        filters,
        setFilters
    } = useLeadsPageContext();

    const filteringByAssignees = !!filters.assigneeIds.length;
    const assigneeFilterLabel = getAssigneesFilterLabel(filters.assigneeIds, teamMembers);

    const filteringByIngestionSources = !!filters.ingestionSources.length;
    const ingestionSourcesFilterLabel = getIngestionSourcesFilterLabel(filters.ingestionSources);

    const filteringByIngestionMethods = !!filters.ingestionMethods.length;
    const ingestionMethodsFilterLabel = useMemo(() => {
        return `Ingestion method is ${filters.ingestionMethods
            .map(method => `"${LEAD_INGESTION_METHOD_OPTIONS.find(option => option.value === method)?.label || method}"`)
            .join(' or ')}`;
    }, [filters.ingestionMethods]);

    const filteringByDateRange = !!filters.dateRange;
    const dateRangeFilterLabel = filters.dateRange && leadDateFilterLabels[filters.dateRange];

    const filteringByLeadStages = !!filters.leadStages.length;
    const leadStagesFilterLabel = getLeadStagesFilterLabel(filters.leadStages);

    const filteringByLeadLabels = !!filters.leadLabelIds.length;
    const leadLabelsFilterLabel = getLeadLabelsFilterLabel(filters.leadLabelIds, leadLabels);

    const filteringByProperty = !!filters.propertyIds.length;
    const propertyFilterLabel = getPropertyFilterLabel(filters.propertyIds, properties);

    const filteringByPropertyLabels = !!filters.propertyLabelIds.length;
    const propertyLabelsFilterLabel = getPropertyLabelsFilterLabel(filters.propertyLabelIds, propertyLabels);

    return (
        <>
            {filteringByAssignees && (
                <Tooltip title={assigneeFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={assigneeFilterLabel}
                            icon={<UsersIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, assigneeIds: [] }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByIngestionSources && (
                <Tooltip title={ingestionSourcesFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={ingestionSourcesFilterLabel}
                            icon={<DownloadRoundedIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, ingestionSources: [] }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByIngestionMethods && (
                <Tooltip title={ingestionMethodsFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={ingestionMethodsFilterLabel}
                            icon={<UsersIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, ingestionMethods: [] }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByDateRange && (
                <Tooltip title={dateRangeFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={dateRangeFilterLabel}
                            icon={<CalendarIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, dateRange: undefined }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByLeadStages && (
                <Tooltip title={leadStagesFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={leadStagesFilterLabel}
                            icon={<LeadsIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, leadStages: [] }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filteringByLeadLabels && (
                <Tooltip title={leadLabelsFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={leadLabelsFilterLabel}
                            icon={<LeadsIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, leadLabelIds: [] }))}
                        />
                    </div>
                </Tooltip>
            )}

            {filters.hasNoProperty && (
                <Chip
                    variant="outlined"
                    label="Lead has no property"
                    icon={<PropertiesIcon />}
                    onDelete={() => setFilters(prev => ({ ...prev, hasNoProperty: false }))}
                />
            )}

            {filteringByProperty && (
                <Tooltip title={propertyFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={propertyFilterLabel}
                            icon={<PropertiesIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, propertyIds: [] }))}
                        />
                    </div>
                </Tooltip>
            )}



            {filteringByPropertyLabels && (
                <Tooltip title={propertyLabelsFilterLabel}>
                    <div>
                        <Chip
                            variant="outlined"
                            label={propertyLabelsFilterLabel}
                            icon={<PropertiesIcon />}
                            onDelete={() => setFilters(prev => ({ ...prev, propertyLabelIds: [] }))}
                        />
                    </div>
                </Tooltip>
            )}
        </>
    );
}
